import MyButton from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Col } from "@/styles/layout";
import { BiSave } from "react-icons/bi";
import { Controller, useForm } from "react-hook-form";
import AmountInput from "@/components/AmountInput";
import { toast } from "react-hot-toast";
import {
  UpdateOfflineDonation,
  deleteOffline,
  updateOfflineDonation,
} from "@/services/donations.service";
import { useCampaigns } from "@/hooks/useCampaigns";
import { Donations } from "@/types/models/Donations";
import TextArea from "@/components/TextArea";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  donation: Donations;
  onSave: () => void;
}

const ModalOfflineDonation = ({ donation, isOpen, onClose, onSave }: Props) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<UpdateOfflineDonation>({
    defaultValues: {
      amount: donation.grossAmount,
      comment: donation.comment,
    },
  });

  const submit = async (input: UpdateOfflineDonation) => {
    try {
      console.log(input);
      await updateOfflineDonation(donation.id, input);
      onSave();
      onClose();
      toast.success("Offline donation updated successfully!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Something went wrong while updating your offline donation..."
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOffline(donation.id);
      onSave();
      onClose();
      toast.success("Donation deleted successfully!");
    } catch (e) {
      toast.error("Failed to delete your offline donation  ");
    }
  };

  return (
    <Dialog
      onOpenChange={(e: boolean) => (!e ? onClose() : undefined)}
      open={isOpen}
    >
      <div className="space-y-5 p-6">
        <div className="text-lg font-semibold text-center">
          Modify an offline donation
        </div>
        <Col py="24px">
          <form onSubmit={handleSubmit(submit)}>
            <Col pb="44px">
              <Controller
                name="amount"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <AmountInput
                    label="Amount"
                    containerStyle={{
                      borderRadius: "4px",
                      borderWidth: "1px",
                      borderColor: "primary.borderSubtle",
                      borderStyle: "solid",
                      padding: "8px",
                      height: 50,
                      marginRight: 16,
                      marginBottom: 24,
                    }}
                    min={5}
                    {...field}
                    value={(field.value ?? 0) / 100}
                    onChange={(e) => field.onChange(e * 100)}
                  />
                )}
              />
              <Controller
                name="comment"
                control={control}
                rules={{ required: false }}
                render={({ field, fieldState: { error } }) => (
                  <TextArea
                    label="Comment"
                    containerStyle={{
                      borderRadius: "4px",
                      borderWidth: "1px",
                      borderColor: "primary.borderSubtle",
                      borderStyle: "solid",
                      padding: "8px",
                      height: 88,
                      marginRight: 16,
                      marginBottom: 24,
                    }}
                    {...field}
                  />
                )}
              />
            </Col>
            <div className="flex flex-col space-y-2 items-center ">
              <MyButton
                variant="primary"
                label="Save"
                isLoading={isSubmitting}
                containerStyle={{
                  height: 44,
                  borderRadius: 8,
                  minWidth: 200,
                  px: 32,
                }}
                labelStyle={{ fontSize: "16px" }}
                Icon={BiSave}
                disabled={isSubmitting || !isValid || !isDirty}
                type="submit"
              />

              <div
                className="text-center text-red-600 font-semibold text-sm underline cursor-pointer"
                onClick={handleDelete}
              >
                Delete
              </div>
            </div>
          </form>
        </Col>
      </div>
    </Dialog>
  );
};

export default ModalOfflineDonation;
